import { Controller } from "@hotwired/stimulus"

// data-controller="clipboard"
//   <div  data-clipboard-target="source">text to copy</div>
//   <button data-action="clipboard#copy"
//           data-clipboard-target="button">Copy</button>
//
// On click, copies the source's text to the clipboard, then briefly flashes
// the button to the brand accent colour + "Copied ✓" label for ~1.5s before
// reverting. Lavender-with-dark-text is the brand's sanctioned accent
// moment; yellow can't be used as a button fill (contrast rule).
//
// Falls back to execCommand("copy") on environments where
// navigator.clipboard isn't available (insecure-context HTTP outside of
// localhost, mostly).
export default class extends Controller {
  static targets = ["source", "button"]
  static values  = {
    confirm: { type: String, default: "Copied ✓" }
  }

  // Tailwind classes that get added during the flash. The button keeps its
  // base classes; these layer on top with `!` important to win the
  // specificity battle against the resting bg-brand-ink text-brand-surface.
  static FLASH_CLASSES = ["!bg-brand-accent", "!text-brand-ink"]

  connect() {
    if (this.hasButtonTarget) {
      this.originalLabel = this.buttonTarget.textContent
    }
  }

  async copy() {
    const text = this.readText()
    if (!text) return

    let ok = false
    try {
      await navigator.clipboard.writeText(text)
      ok = true
    } catch {
      ok = this.fallbackCopy(text)
    }
    if (ok) this.flashConfirmation()
  }

  readText() {
    if (!this.hasSourceTarget) return ""
    const el = this.sourceTarget
    // Prefer .value (inputs/textareas) only when it's non-empty; otherwise
    // read textContent so a <div>/<pre>/<code> source works too.
    const v = ("value" in el) ? el.value : null
    if (typeof v === "string" && v.length > 0) return v
    return (el.textContent || "").trim()
  }

  flashConfirmation() {
    if (!this.hasButtonTarget) return
    this.buttonTarget.textContent = this.confirmValue
    this.buttonTarget.classList.add(...this.constructor.FLASH_CLASSES)
    clearTimeout(this.resetTimer)
    this.resetTimer = setTimeout(() => {
      this.buttonTarget.textContent = this.originalLabel
      this.buttonTarget.classList.remove(...this.constructor.FLASH_CLASSES)
    }, 1500)
  }

  // Last-resort path for environments where navigator.clipboard isn't
  // available. Hidden textarea + select + document.execCommand("copy").
  // Deprecated but still works in every browser that ever shipped.
  fallbackCopy(text) {
    const ta = document.createElement("textarea")
    ta.value = text
    ta.style.position = "fixed"
    ta.style.opacity = "0"
    document.body.appendChild(ta)
    ta.select()
    let ok = false
    try { ok = document.execCommand("copy") } catch {}
    document.body.removeChild(ta)
    return ok
  }
}
